

    import Vue from "vue";
    import Component from "vue-class-component";
    import { Ref, Watch } from "vue-property-decorator";
    import router, { beforeRouteLeave } from "@/router/router";
    import { VForm } from "@/vForm";
    import draggable from "vuedraggable";
    import * as toastr from "toastr";
    import ApiClient from "@/utilities/ApiClient";
    import Utils from "@/utilities/Utils";
    import { appEventBus } from "@/utilities/EventBus";
    import Dirtyness from "@/utilities/Dirtyness";
    import ApiButton from "@/vue/components/ApiButton.vue";
    import PrimaryButton from "@/vue/components/PrimaryButton.vue";
    import ConfirmDialogue from "@/vue/components/ConfirmDialogue.vue";
    import AlertDialogue from "@/vue/components/AlertDialogue.vue";
    import DatePicker from "@/vue/components/DatePicker.vue";
    import FileUpload from "@/vue/components/FileUpload.vue";
    import HtmlEditor from "@/vue/components/HtmlEditor.vue";
    import VisitorCompanyList from "@/vue/components/VisitorCompanyList.vue";
    import SiteRecipientList from "@/vue/components/SiteRecipientList.vue";
    import SiteLogEntryList from "@/vue/components/SiteLogEntryList.vue";
    import PermitLocationList from "@/vue/components/PermitLocationList.vue";
    import PermitZoneList from "@/vue/components/PermitZoneList.vue";
    import FieldDependencyDialogue from "@/vue/components/FieldDependencyDialogue.vue";
    import PathwayFilterDialogue from "@/vue/components/PathwayFilterDialogue.vue";
    import { RegistrationFieldType, SiteRegistrationType, TriState, WorkPermitFormType } from "@/model/Enums";
    import { IClientLookup } from "@/model/Client";
    import { ISite, Site } from "@/model/Site";
    import { StandardRegistrationField, RequirementType } from "@/model/StandardRegistrationField";
    import { ExtraRegistrationField } from "@/model/ExtraRegistrationField";
    import { ILookupItem, LookupItem } from "@/model/LookupItem";
    import { IBriefingPathway } from "@/model/BriefingPathway";
    import { ISaveResponse } from "@/model/ISaveResponse";
    import { ISiteRecipient } from "@/model/SiteRecipient";
    import { SiteRecipientSearchParameters } from "@/model/SiteRecipientSearchParameters";

    interface ISiteCounts {
        visitorCompaniesText: string;
        siteRecipientsText: string;
        visitorCompanies: number;
        sponsors: number;
        siteLogEntriesText: string;
    }

    interface ISiteCountsResponse {
        visitorCompanies: number;
        siteRecipients: number;
        sponsors: number;
        siteLogEntries: number;
    }

    interface IDragChangeEvent {
        added?: {
            element: StandardRegistrationField|ExtraRegistrationField;
            newIndex: number;
        };
        removed?: {
            element: StandardRegistrationField|ExtraRegistrationField;
            oldIndex: number;
        };
        moved?: {
            element: StandardRegistrationField|ExtraRegistrationField;
            oldIndex: number;
            newIndex: number;
        };
    }

    interface IKeyedFieldType {
        key: string;
        fieldType: RegistrationFieldType;
    }

    // cut-down version of SiteRecipient - for warnings
    interface IRecipientPathways {
        id: string;
        isCarbonCopyAllPathways: boolean;
        pathwayIDs: Array<string>;
    }

    export enum WarningsStage {
        None = 0,
        TopOfPage,
        ActiveBriefingChangeToast,
        SaveConfirm
    }

    @Component({
        components: {
            draggable,
            ApiButton,
            PrimaryButton,
            AlertDialogue,
            ConfirmDialogue,
            DatePicker,
            FileUpload,
            HtmlEditor,
            VisitorCompanyList,
            SiteRecipientList,
            SiteLogEntryList,
            PermitLocationList,
            PermitZoneList,
            FieldDependencyDialogue,
            PathwayFilterDialogue
        },
        beforeRouteLeave
    })
    export default class SiteComponent extends Vue {

        async mounted(): Promise<void> {
            const siteID = this.$route.params.siteID ?? Utils.emptyGuidValue;

            this.loadEnumOptions();

            if(this.$route.query["tab"] === "recipients") {
                this.tabName = "tab-recipient-list";
                // remove query from URL without navigating (manipulating URL like this and not using router seems such a shitty hack)
                let url = window.location.href;
                url = url.replace("?tab=recipients", "");
                history.replaceState({}, document.title, url);
                // doesn't seem to work how we want:
                // const query = Object.assign({}, this.$route.query);
                // delete query["tab"];
                // this.$router.replace({ query });
                toastr.info("Use browser back button to return...");
            }

            // fire off all the loads in parallel (TODO - reload briefings and pathways if client changes + filter for client...)
            await Promise.all([
                this.loadSite(siteID),
                (async () => { this.clientList = await ApiClient.get("/api/client/lookups"); })(),
                (async () => { this.briefingList = await ApiClient.get(`/api/briefing/lookups?siteID=${siteID}`); })(),
                (async () => { this.pathwayList = await ApiClient.get(`/api/pathway/briefingPathways?siteID=${siteID}`); })()
            ]);

            // some extra stuff after the site has loaded
            await Promise.all([
                this.loadSiteCounts(),
                this.loadRecipientPathways()
            ]);
        }

        clientList: Array<IClientLookup> = [];

        get selectedClient(): IClientLookup | undefined {
            return this.clientList.find(c => c.id === this.site.clientID);
        }

        get clientName(): string {
            return this.selectedClient?.description ?? "- - -";
        }

        get workPermitFormTypesOptions(): Array<ILookupItem> {
            const translate = (text: string): string => {
                if(text ==="SouthEastern") return "South Eastern"
                if(text ==="SwmScapa") return "SWM Scapa"
                return text;
            };
            return ([WorkPermitFormType.None, ...this.selectedClient?.workPermitFormTypes ?? []])
                .map(i => LookupItem.createLookup(i, translate(WorkPermitFormType[i])));
        }

        get canSelectWorkPermitForm(): boolean {
            return (this.selectedClient?.workPermitFormTypes ?? []).length > 0;
        }

        get hasSelectedWorkPermitForm(): boolean {
            return this.site.workPermitFormType > WorkPermitFormType.None;
        }

        briefingList: Array<ILookupItem> = [];
        get briefingOptions(): Array<ILookupItem> { return Utils.optionsFilter(this.briefingList); } // TODO - this should be filtered for client!

        pathwayList: Array<IBriefingPathway> = [];

        get registrationTypeOptions(): Array<ILookupItem> { return Utils.enumToLookups(SiteRegistrationType).filter(i => i.id !== 0); }

        site: Site = new Site();

        recipientPathways: Array<IRecipientPathways> = [];

        isDirty: boolean = false;
        readonly dirtynessOptions = {
            model: this.site,
            isDeep: true,
            excludeList: [...Dirtyness.defaultAuditFieldList()]
        };

        counts: ISiteCounts = {
            visitorCompaniesText: "-",
            siteRecipientsText: "-",
            visitorCompanies: 0,
            sponsors: 0,
            siteLogEntriesText: "-"
        };

        chosenFile: File|null = null;

        @Ref("siteForm") readonly siteForm!: VForm;

        extraFieldTypeOptions: Array<ILookupItem> = [];

        standardFieldRequirementOptions: Array<ILookupItem> = [];

        isMandatoryOptions: Array<({ value: boolean; description: string })> = [
            { value: true, description: "Yes" },
            { value: false, description: "No" }
        ];

        tabName: string = "tab-details";

        get title(): string {
            return Utils.isEmptyId(this.site.id) ? "New Site" : `Edit Site - ${this.site.name}`;
        }

        get canDelete(): boolean {
            return !Utils.isEmptyId(this.site.id);
        }

        get warningsHtml(): string {
            const warnings = this.getWarnings(WarningsStage.TopOfPage);
            if(warnings.length === 0) return "";
            if(warnings.length === 1) return warnings[0];
            return `<ul><li>${warnings.join("</li><li>")}</li></ul>`;
        }

        get locationsCount(): string {
            const subCount = this.site.permitLocations.reduce((accumulator, loc) => accumulator + loc.subLocations.length, 0);
            return `${this.site.permitLocations.length} / ${subCount}`;
        }

        get zonesCount(): string {
            return `${this.site.permitZones.length}`;
        }

        get qrCodeUrl(): string {
            return ApiClient.resolveUrl(`api/site/qrcode?siteID=${this.site.id}`);
        }

        get qrCodeFileName(): string {
            return `qr-code-${Utils.dashify(this.site.name)}.png`
        }

        @Watch("site", { deep: true, immediate: false })
        onSiteChanged() {
            this.isDirty = Dirtyness.dataChanged(this.dirtynessOptions);
        }

        @Watch("site.activeBriefingID", { deep: false, immediate: false })
        onActiveBriefingChanged() {
            // update isActiveBriefing flags for pathwayList
            for(let i = 0; i < this.pathwayList.length; i++) {
                this.pathwayList[i].isActiveBriefing = this.pathwayList[i].briefingID === this.site.activeBriefingID;
            }
            const warnings = this.getWarnings(WarningsStage.ActiveBriefingChangeToast);
            if(warnings.length > 0) {
                const message = warnings.length === 1
                    ? warnings[0]
                    : `<ul style="margin-left:30px"><li>${warnings.join("</li><li>")}</li></ul>`;
                toastr.warning(message, "Active Briefing Changed", { escapeHtml: false });
            }
        }

        async onVisitorCompanyListChanged(): Promise<void> {
            await this.loadSiteCounts();
        }

        async onSiteRecipientListChanged(): Promise<void> {
            await Promise.all([
                this.loadSiteCounts(),
                this.loadRecipientPathways()
            ]);
        }

        async onSiteLogEntryListChanged(): Promise<void> {
            await this.loadSiteCounts();
        }

        canChangeRequirement(item: StandardRegistrationField): boolean {
            return item.columnName !== "email"
                && item.columnName !== "forename"
                && item.columnName !== "surname"
                && item.columnName !== "pathwayID";
        }

        async loadSite(siteID: string): Promise<void> {
            Utils.resetObject(this.site);
            // still go off to server, even for new stuff (see SiteLogic)
            const siteData: ISite = await ApiClient.get(`api/site/Load?id=${siteID}`);
            //this.oldActiveBriefingID = this.site.activeBriefingID;
            this.site.update(siteData);
            this.siteForm?.resetValidation();
            Dirtyness.setClean(this.dirtynessOptions);
        }

        async loadSiteCounts(): Promise<void> {
            if(Utils.isEmptyId(this.site.id)) {
                this.counts.visitorCompaniesText = "-";
                this.counts.siteRecipientsText = "-";
                this.counts.siteLogEntriesText = "-";
                return;
            }
            const counts: ISiteCountsResponse = await ApiClient.get(`api/site/Counts?siteID=${this.site.id}`);
            this.counts.visitorCompaniesText = counts.visitorCompanies.toString();
            this.counts.siteRecipientsText = counts.siteRecipients.toString();
            this.counts.siteLogEntriesText = counts.siteLogEntries.toString();
            this.counts.visitorCompanies = counts.visitorCompanies;
            this.counts.sponsors = counts.sponsors;
        }

        // just for the warnings
        async loadRecipientPathways(): Promise<void> {
            this.recipientPathways = [];
            if(this.site.isNew) return;
            const parameters = new SiteRecipientSearchParameters();
            parameters.siteID = this.site.id;
            parameters.isDeleted = TriState.False;
            const data = await ApiClient.post("/Api/SiteRecipient/Search", parameters) as Array<ISiteRecipient>;
            this.recipientPathways = data.filter(r => r.isActive && !Utils.hasDateValue(r.deleted)).map(r => ({
                id: r.id,
                isCarbonCopyAllPathways: r.isCarbonCopyAllPathways,
                pathwayIDs: r.pathwayIDs
            }));
        }

        loadEnumOptions(): void {
            const translate = (text: string): string => {
                if(text ==="YesNo") return "Yes or No"
                if(text ==="SingleText") return "Single Line"
                if(text ==="MultiLineText") return "Multi Line"
                return text;
            };
            this.extraFieldTypeOptions = Object.entries(RegistrationFieldType)
                .filter(([_, b]) => typeof b === "number" )
                .map(([text, number]) => LookupItem.createLookup(number, translate(text)));
            this.standardFieldRequirementOptions = Object.entries(RequirementType)
                .filter(([_, b]) => typeof b === "number" )
                .map(([text, number]) => LookupItem.createLookup(number, translate(text)));
        }

        goToList(): void {
            this.$router.push({ name: "AdminSites" });
        }

        addExtraRegistrationField(): void {
            this.site.extraRegistrationFields.push(ExtraRegistrationField.createNew());
        }

        removeExtraRegistrationField(item: ExtraRegistrationField): void {
            const index = this.site.extraRegistrationFields.indexOf(item);
            if (index > -1) this.site.extraRegistrationFields.splice(index, 1);
        }

        async onDragChange(changeEvent:  IDragChangeEvent): Promise<void> {
            if(!changeEvent.moved) return;
            // we don't actually need to do anything here! (saving site will update the order)
            console.log("drag-moved");
        }

        validateWorkPermitPrefix(value: string|null|undefined): boolean|string {
            if (this.hasSelectedWorkPermitForm && Utils.isEmptyOrWhitespace(this.site.workPermitPrefix)) {
                return "Prefix required if using Work Permits";
            }
            return true;
        }

        validateFailureTemplate(value: string|null|undefined): boolean|string {
            if(!value) return "Please specify a template";
            if(value.indexOf("{url}") === -1) return "The {url} placeholder is maissing";
            if(value.indexOf("{forename}") === -1) return "The {forename} placeholder is maissing";
            if(value.indexOf("{surname}") === -1) return "The {surname} placeholder is maissing";
            return true;
        }

        validateRegistrationTemplate(value: string|null|undefined): boolean|string {
            if(!value) return "Please specify a template";
            if(value.indexOf("{url}") === -1) return "The {url} placeholder is maissing";
            if(value.indexOf("{forename}") === -1) return "The {forename} placeholder is maissing";
            if(value.indexOf("{surname}") === -1) return "The {surname} placeholder is maissing";
            return true;
        }

        // warn the user that something might be dodgy but will still allow them to do whatever it is...
        getWarnings(stage: WarningsStage): Array<string> {
            const isSponsor = this.site.standardRegistrationFields.find(f => f.columnName === "sponsorID")?.isHidden !== true;
            const isCompany = this.site.standardRegistrationFields.find(f => f.columnName === "companyID")?.isHidden !== true;
           // const activeBriefingChanged = this.oldActiveBriefingID !== this.site.activeBriefingID;
            const warnings: Array<string> = [];

            // skip page warning if site hasn't been saved yet
            if(stage === WarningsStage.TopOfPage && this.site.isNew) return warnings;

            // don't show toast for sponsors and companies warnings
            if(stage !== WarningsStage.ActiveBriefingChangeToast && this.counts.sponsors === 0 && isSponsor) {
                warnings.push("Registration includes <b>sponsor</b> but there are no sponsors for the site.");
            }
            if(stage !== WarningsStage.ActiveBriefingChangeToast && this.counts.visitorCompanies === 0 && isCompany) {
                warnings.push("Registration includes <b>company</b> but there are no visitor companies for the site.");
            }

            if(this.site.extraRegistrationFields.some(erf => !erf.isAllPathways && this.isPotentialPathwaySelectionProblem(erf.pathwayIds))) {
                if(stage === WarningsStage.TopOfPage)
                    warnings.push("There is at least one <b>extra registration field</b> for which there are pathway filters but not for the <b>active briefing</b>.");
                else if(stage === WarningsStage.ActiveBriefingChangeToast)
                    warnings.push("If you change the active briefing, you should check pathway filters for the <b>extra registrations fields</b>.");
                else if(stage === WarningsStage.SaveConfirm)
                    warnings.push("The active briefing has changed but some <b>extra registrations fields</b> are filtered on what will now be inactive pathways.");
            }
            if(this.recipientPathways.some(rp => !rp.isCarbonCopyAllPathways && this.isPotentialPathwaySelectionProblem(rp.pathwayIDs))) {
                if(stage === WarningsStage.TopOfPage)
                    warnings.push("There is at least one <b>recipient CC</b> for which there are pathway filters but not for the <b>active briefing</b>.");
                else if(stage === WarningsStage.ActiveBriefingChangeToast)
                    warnings.push("If you change the active briefing, you should check pathway filters for the <b>recipient CCs</b>.");
                else if(stage === WarningsStage.SaveConfirm)
                    warnings.push("The active briefing has changed but some <b>recipient CCs</b> are filtered on what will now be inactive pathways.");
            }
            return warnings;
        }

        async confirmSave(): Promise<void> {
            const dialogue: ConfirmDialogue = this.$refs.saveDialogue as ConfirmDialogue;
            const warnings = this.getWarnings(WarningsStage.SaveConfirm);
            if(warnings.length > 0) {
                dialogue.title = `<i class="mr-2 mdi mdi-alert-circle"></i>${warnings.length === 1 ? "Warning" : "Warnings"}`;
                dialogue.text = warnings.length === 1
                    ? warnings[0]
                    : `<ul><li>${warnings.join("</li><li>")}</li></ul>`;
                dialogue.show();
            }
            else {
                await this.save();
            }
        }

        async save(): Promise<void> {
            // hide if showing...
            const dialogue: ConfirmDialogue = this.$refs.saveDialogue as ConfirmDialogue;
            dialogue.hide();

            if (!this.siteForm.validate()) {
                toastr.warning("Please fix highlighted issues", "Cannot Save");
                return;
            }

            // fix up the extra fields
            this.site.extraRegistrationFields = this.site.extraRegistrationFields
                .filter(f => f.fieldType != RegistrationFieldType.None &&
                    !Utils.isEmptyOrWhitespace(f.label));
            this.removeExtraFieldsInvalidDependencies();

            const response: ISaveResponse = await ApiClient.post("/api/site/save", this.site, "site-save");
            if(this.site.isNew) this.site.id = response.newID;
            this.isDirty = Dirtyness.setClean(this.dirtynessOptions);
            toastr.success("Saved");
        }

        confirmDelete(): void {
            const dialog: ConfirmDialogue = this.$refs.deleteDialogue as ConfirmDialogue;
            dialog.show();
        }

        async doDelete(): Promise<void> {
            await ApiClient.post("/api/site/delete", this.site, "site-delete");

            // hmmm... (just to trigger toast on site list)
            setTimeout(() => {
                appEventBus.emit("deleted-site", this.site.name);
            }, 500);

            this.goToList();
        }

        //
        // -- field dependencies
        //

        shouldShowFieldDependencyDialogue: boolean = false;
        selectedDependencyField: ExtraRegistrationField | null = null;
        dependencyAlertDialogueContent: string = "";

        dependencyIconColour(item: ExtraRegistrationField): string {
            return item.fieldType === RegistrationFieldType.YesNo
                ? "#EEE"
                : item.showIf.hasValue
                   ? "#36F"
                   : "#AAA";
            //this.site.extraRegistrationFields.some(i => i.fieldType === RegistrationFieldType.YesNo);
        }

        dependencyIconToolTip(item: ExtraRegistrationField): string {
            return item.fieldType === RegistrationFieldType.YesNo
                ? "This is a yes-or-no field."
                : item.showIf.hasValue
                    ? `Depends on '${ this.site.extraRegistrationFields.find(i => i.key === item.showIf.key)?.label ?? "- - -"}'`
                    : "Always show";
        }

        // this is a hack (?) so we can use @Watch to detect in any field types changed
        get extraFieldTypes(): Array<IKeyedFieldType> {
            return this.site.extraRegistrationFields.map(i => ({ key: i.key, fieldType: i.fieldType }));
        }

        @Watch("extraFieldTypes", { deep: false, immediate: false })
        onExtraRegistrationFieldTypeChanged(afters: Array<IKeyedFieldType>, befores: Array<IKeyedFieldType>) {
            // are we changing a yes/no field which has a dependency into not a yes/no field?
            for(const before of befores) {
                if(before.fieldType !== RegistrationFieldType.YesNo) continue;
                const after = afters.find(i => i.key = before.key);
                if(!after || before.fieldType === after.fieldType) continue;
                const hasDependency = this.site.extraRegistrationFields.some(i => i.showIf.key === before.key);
                if(!hasDependency) continue;
                const label = this.site.extraRegistrationFields.find(i => i.key = before.key)?.label ?? "(???)";
                this.dependencyAlertDialogueContent = `The field '<b>${label}</b>' was a <b>yes-or-no</b> field.  It has show/hide dependencies on it which will be <b>removed</b> if you save this change!`;
                (this.$refs.dependencyWarningDialogue as AlertDialogue).show();
                return;
            }
        }

        showDependencyDialogue(item: ExtraRegistrationField) {
            this.selectedDependencyField = item;
            this.shouldShowFieldDependencyDialogue = true;
        }

        onFieldDependencyChanged(field: ExtraRegistrationField, fieldKey: string, fieldValue: string) {
            if(!this.selectedDependencyField) return;
            if(!Utils.isEmptyOrWhitespace(fieldKey) && !Utils.isEmptyOrWhitespace(fieldValue)) {
                this.selectedDependencyField.showIf.key = fieldKey;
                this.selectedDependencyField.showIf.value = fieldValue;
            }
            else {
                this.selectedDependencyField.showIf.key = "";
                this.selectedDependencyField.showIf.value = "";
            }
        }

        removeExtraFieldsInvalidDependencies() {
            const yesNoKeys = this.site.extraRegistrationFields
                .filter(i => i.fieldType === RegistrationFieldType.YesNo)
                .map(i => i.key);
            for(const extraField of this.site.extraRegistrationFields) {
                if(extraField.showIf.key &&
                    (!yesNoKeys.some(i => extraField.showIf.key === i) ||
                         extraField.fieldType === RegistrationFieldType.YesNo)) {
                    extraField.showIf.key = "";
                    extraField.showIf.value = "";
                }
            }
        }

        //
        // -- pathway filter
        //

        shouldShowPathwayFilterDialogue: boolean = false;

        selectedPathwayFilterField: ExtraRegistrationField | null = null;

        pathwayFilterIconColour(item: ExtraRegistrationField): string {
            return (!item.isAllPathways && item.pathwayIds?.length > 0) ? "#36F" : "#AAA";
        }

        pathwayFilterIconToolTip(item: ExtraRegistrationField): string {
            if (item.isAllPathways) return "Applies to ALL pathways";
            if ((item.pathwayIds?.length ?? 0) === 0) return "No filter - applies to ALL pathways";
            const pathways = this.pathwayList.filter(p => item.pathwayIds?.indexOf(p.pathwayID) > -1);
            if(pathways.length === 0) return "Applies to ALL pathways";
            let text = "Only pathways:<br>" + pathways.map(p => `&nbsp;&nbsp;${p.briefingName} - <b>${p.pathwayDescription}</b>`).join("<br>");
            return text;
        }

        showPathwayFilterDialogue(item: ExtraRegistrationField) {
            this.selectedPathwayFilterField = item;
            this.shouldShowPathwayFilterDialogue = true;
        }

        onFieldPathwayClearAll() {
            if(!this.selectedPathwayFilterField) return;
            this.selectedPathwayFilterField.pathwayIds.splice(0);
        }

        onFieldPathwayChanged(pathwayID: string) {
            if(!this.selectedPathwayFilterField) return;
            const idx = this.selectedPathwayFilterField.pathwayIds.indexOf(pathwayID);
            if(idx > -1) {
                this.selectedPathwayFilterField.pathwayIds.splice(idx, 1);
            }
            else {
                this.selectedPathwayFilterField.pathwayIds.push(pathwayID);
            }
        }

        // see if there are any pathways selected - but none for active briefing
        isPotentialPathwaySelectionProblem(pathwayIDs: Array<string>): boolean {
            if((pathwayIDs?.length ?? 0) === 0) return false;
            const areActivePathwaysSelected = this.pathwayList.some(p => p.isActiveBriefing && pathwayIDs.indexOf(p.pathwayID) > -1);
            const areNonActivePathwaysSelected = this.pathwayList.some(p => !p.isActiveBriefing && pathwayIDs.indexOf(p.pathwayID) > -1);
            return !areActivePathwaysSelected && areNonActivePathwaysSelected;
        }
    }
